import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { X, Printer, RefreshCw } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const fmt$ = (n) => `$${Number(n || 0).toFixed(2)}`;
const CT = 'America/Chicago';

// ---- 7am→7am Central business-day helpers ----
// A "business date" is the CT calendar date the 7am→7am window STARTS on.
const ctDateStr = (d) => new Intl.DateTimeFormat('en-CA', {
    timeZone: CT, year: 'numeric', month: '2-digit', day: '2-digit',
}).format(d);

const shiftDate = (ymd, delta) => {
    const [y, m, d] = ymd.split('-').map(Number);
    const dt = new Date(Date.UTC(y, m - 1, d));
    dt.setUTCDate(dt.getUTCDate() + delta);
    return dt.toISOString().slice(0, 10);
};

// The business date of the LAST COMPLETED window (the one the operator prints
// each morning). At 7:22am Sep 3 CT this is Sep 2 (Sep 2 7am → Sep 3 7am).
const lastCompletedBusinessDate = () => {
    const now = new Date();
    const hourCT = Number(new Intl.DateTimeFormat('en-US', {
        timeZone: CT, hour: 'numeric', hour12: false,
    }).formatToParts(now).find((p) => p.type === 'hour')?.value || 0);
    const anchorDate = hourCT < 7 ? shiftDate(ctDateStr(now), -1) : ctDateStr(now);
    return shiftDate(anchorDate, -1);
};

const labelFor = (ymd) => {
    const fmt = (s) => {
        const [y, m, d] = s.split('-').map(Number);
        return new Intl.DateTimeFormat('en-US', {
            timeZone: 'UTC', weekday: 'short', month: 'short', day: 'numeric',
        }).format(new Date(Date.UTC(y, m - 1, d, 12)));
    };
    return {
        start: `${fmt(ymd)} · 7:00 AM CT`,
        end: `${fmt(shiftDate(ymd, 1))} · 7:00 AM CT`,
    };
};

const nowStamp = () => new Intl.DateTimeFormat('en-US', {
    timeZone: CT, dateStyle: 'medium', timeStyle: 'short',
}).format(new Date());

/**
 * EndOfDaySheet — one-tap printable accounting summary for the current
 * 7am→7am Central business day. Opens as an on-screen preview; the Print
 * button renders the sheet into a hidden iframe and prints only that (so the
 * rest of the admin UI never bleeds into the printout).
 */
const EndOfDaySheet = ({ onClose }) => {
    const maxDate = lastCompletedBusinessDate();
    const [businessDate, setBusinessDate] = useState(maxDate);
    const [today, setToday] = useState(null);
    const [operator, setOperator] = useState('');
    const [loading, setLoading] = useState(true);
    const [err, setErr] = useState('');
    const day = labelFor(businessDate);

    const load = useCallback(async () => {
        setLoading(true);
        setErr('');
        try {
            const { data } = await axios.get(`${API}/admin/reports/financial`, {
                params: { eod_date: businessDate }, withCredentials: true,
            });
            setToday(data.eod || {});
        } catch {
            setErr('Could not load the day\u2019s numbers. Try again.');
        } finally {
            setLoading(false);
        }
    }, [businessDate]);

    // Refetch numbers whenever the selected business day changes.
    useEffect(() => { load(); }, [load]);

    // Operator name is a fast, optional background fetch.
    useEffect(() => {
        let alive = true;
        axios.get(`${API}/auth/me`, { withCredentials: true })
            .then((r) => { if (alive) setOperator(r.data?.name || r.data?.email || ''); })
            .catch(() => { /* name is optional */ });
        return () => { alive = false; };
    }, []);

    const rows = today ? buildRows(today) : null;

    const handlePrint = () => {
        if (!rows) return;
        const html = printableHtml({ rows, day, operator });
        const frame = document.createElement('iframe');
        frame.style.position = 'fixed';
        frame.style.right = '0';
        frame.style.bottom = '0';
        frame.style.width = '0';
        frame.style.height = '0';
        frame.style.border = '0';
        document.body.appendChild(frame);
        const doc = frame.contentWindow.document;
        doc.open();
        doc.write(html);
        doc.close();
        const cleanup = () => setTimeout(() => document.body.removeChild(frame), 500);
        frame.contentWindow.focus();
        frame.contentWindow.onafterprint = cleanup;
        setTimeout(() => { frame.contentWindow.print(); }, 250);
        setTimeout(cleanup, 60000);
    };

    return (
        <div
            className="fixed inset-0 z-[10000] flex items-start justify-center bg-black/70 backdrop-blur-sm p-3 sm:p-6 overflow-y-auto"
            data-testid="eod-sheet-overlay"
            onMouseDown={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
        >
            <div className="w-full max-w-2xl rounded-2xl border border-[#FFD700]/30 bg-[#0b0b0f] shadow-2xl my-auto" data-testid="eod-sheet">
                <div className="flex items-center justify-between px-4 sm:px-5 py-3 border-b border-white/10">
                    <div>
                        <h2 className="text-base sm:text-lg font-black tracking-wide text-[#FFD700]">END-OF-DAY SHEET</h2>
                        <p className="text-[11px] text-white/50">{day.start} &rarr; {day.end}</p>
                    </div>
                    <div className="flex items-center gap-2">
                        <input
                            type="date"
                            value={businessDate}
                            max={maxDate}
                            onChange={(e) => e.target.value && setBusinessDate(e.target.value)}
                            className="bg-black/40 border border-white/15 rounded-lg text-white text-xs px-2 py-1.5 [color-scheme:dark] focus:outline-none focus:border-[#FFD700]/50"
                            title="View a past business day"
                            data-testid="eod-date-picker"
                        />
                        <button
                            onClick={load}
                            disabled={loading}
                            className="p-2 rounded-lg hover:bg-white/10 text-white/60 disabled:opacity-40"
                            title="Refresh"
                            data-testid="eod-refresh"
                        >
                            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                        </button>
                        <button
                            onClick={handlePrint}
                            disabled={loading || !rows}
                            className="flex items-center gap-2 rounded-lg bg-[#FFD700] text-black font-black text-xs px-3 py-2 hover:brightness-110 disabled:opacity-40"
                            data-testid="eod-print-btn"
                        >
                            <Printer className="w-4 h-4" /> PRINT
                        </button>
                        <button
                            onClick={onClose}
                            className="p-2 rounded-lg hover:bg-white/10 text-white/60"
                            data-testid="eod-close"
                            aria-label="Close"
                        >
                            <X className="w-4 h-4" />
                        </button>
                    </div>
                </div>

                <div className="p-4 sm:p-5">
                    {loading && !rows && <p className="text-white/60 text-sm py-8 text-center" data-testid="eod-loading">Loading the day&rsquo;s numbers&hellip;</p>}
                    {err && !rows && <p className="text-[#FF3B30] text-sm py-8 text-center" data-testid="eod-error">{err}</p>}
                    {rows && (
                        <div className="space-y-5" data-testid="eod-content">
                            <p className="text-[11px] text-white/40">
                                Generated {nowStamp()}{operator ? ` · by ${operator}` : ''}
                            </p>

                            <Section title="Money" testid="eod-money">
                                <Line label="Money In (real loads)" value={fmt$(rows.money.in)} pos />
                                <Line label="Money Out (cashouts paid)" value={fmt$(rows.money.out)} neg />
                                <Line label="Net (in − out)" value={`${rows.money.net >= 0 ? '+' : '-'}${fmt$(Math.abs(rows.money.net))}`} strong pos={rows.money.net >= 0} neg={rows.money.net < 0} />
                                <Line label="Cashout fees collected" value={fmt$(rows.money.fees)} />
                                <Line label={`Admin adjustments (${rows.money.adjCount})`} value={fmt$(rows.money.adjustments)} testid="eod-adjustments" />
                                <Line label="Free play issued" value={fmt$(rows.money.freePlay)} />
                                <Line label="Played back" value={fmt$(rows.money.playedBack)} />
                                <Line label={`Pending payouts (${rows.money.pendingCount})`} value={fmt$(rows.money.pending)} />
                            </Section>

                            <Section title="Games — Wagered vs Paid" testid="eod-games">
                                <div className="grid grid-cols-[1fr_auto_auto_auto] gap-x-3 gap-y-1.5 text-xs">
                                    <span className="text-white/40 font-bold uppercase tracking-wider text-[10px]">Game</span>
                                    <span className="text-white/40 font-bold uppercase tracking-wider text-[10px] text-right">Wagered</span>
                                    <span className="text-white/40 font-bold uppercase tracking-wider text-[10px] text-right">Paid</span>
                                    <span className="text-white/40 font-bold uppercase tracking-wider text-[10px] text-right">House</span>
                                    {rows.games.map((g) => (
                                        <GameRow key={g.key} g={g} />
                                    ))}
                                    <span className="text-white font-black text-sm border-t border-white/10 pt-1.5">Total</span>
                                    <span className="text-white/80 font-mono text-right border-t border-white/10 pt-1.5">{fmt$(rows.total.wagered)}</span>
                                    <span className="text-white/80 font-mono text-right border-t border-white/10 pt-1.5">{fmt$(rows.total.paid)}</span>
                                    <span className={`font-mono font-black text-right border-t border-white/10 pt-1.5 ${rows.total.margin >= 0 ? 'text-[#39FF14]' : 'text-[#FF3B30]'}`}>{fmt$(rows.total.margin)}</span>
                                </div>
                            </Section>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

const Section = ({ title, children, testid }) => (
    <div data-testid={testid}>
        <h3 className="text-[11px] font-black tracking-widest uppercase text-white/40 mb-2">{title}</h3>
        <div className="rounded-xl border border-white/10 bg-white/[0.02] p-3 sm:p-4 space-y-1.5">{children}</div>
    </div>
);

const Line = ({ label, value, strong, pos, neg, testid }) => (
    <div className="flex items-center justify-between" data-testid={testid}>
        <span className={`text-xs ${strong ? 'font-black text-white' : 'text-white/70'}`}>{label}</span>
        <span className={`font-mono ${strong ? 'text-sm font-black' : 'text-xs'} ${pos ? 'text-[#39FF14]' : neg ? 'text-[#FF3B30]' : 'text-white/90'}`}>{value}</span>
    </div>
);

const GameRow = ({ g }) => (
    <>
        <span className="text-white/70">{g.label}</span>
        <span className="text-white/60 font-mono text-right">{fmt$(g.wagered)}</span>
        <span className="text-white/60 font-mono text-right">{fmt$(g.paid)}</span>
        <span className={`font-mono text-right ${g.profit >= 0 ? 'text-[#39FF14]' : 'text-[#FF3B30]'}`}>{fmt$(g.profit)}</span>
    </>
);

function buildRows(t) {
    const money = {
        in: Number(t?.deposits?.total || 0),
        out: Number(t?.withdrawals?.total || 0),
        fees: Number(t?.cashout_fees?.total || 0),
        adjustments: Number(t?.adjustments?.total || 0),
        adjCount: Number(t?.adjustments?.count || 0),
        freePlay: Number(t?.free_play?.total || 0),
        playedBack: Number(t?.played_back?.total || 0),
        pending: Number(t?.pending_cashouts?.total || 0),
        pendingCount: Number(t?.pending_cashouts?.count || 0),
    };
    money.net = money.in - money.out;

    const g = (key, label, node) => ({
        key, label,
        wagered: Number(node?.wagered || 0),
        paid: Number(node?.paid_out || 0),
        profit: Number(node?.profit ?? (Number(node?.wagered || 0) - Number(node?.paid_out || 0))),
    });
    const games = [
        g('slots', 'Slots', t?.slots),
        g('keno', 'Keno', t?.keno),
        g('casino', 'Casino (Majik)', t?.casino),
        g('blackjack', 'Blackjack', t?.blackjack),
        g('three_card_poker', 'Three Card Poker', t?.three_card_poker),
        g('craps', 'Craps', t?.craps),
        g('roulette', 'Roulette', t?.roulette),
        g('lottery', 'Lottery', t?.lottery),
        g('sports', 'Sports', t?.sports),
    ];
    const total = games.reduce((acc, g) => ({
        wagered: acc.wagered + g.wagered,
        paid: acc.paid + g.paid,
        margin: acc.margin + g.profit,
    }), { wagered: 0, paid: 0, margin: 0 });
    return { money, games, total };
}

function printableHtml({ rows, day, operator }) {
    const money = rows.money;
    const gameRows = rows.games.map((g) => `
        <tr>
            <td>${g.label}</td>
            <td class="num">${fmt$(g.wagered)}</td>
            <td class="num">${fmt$(g.paid)}</td>
            <td class="num ${g.profit >= 0 ? 'pos' : 'neg'}">${fmt$(g.profit)}</td>
        </tr>`).join('');
    return `<!doctype html><html><head><meta charset="utf-8"><title>End of Day — ${day.start}</title>
    <style>
      * { box-sizing: border-box; }
      body { font-family: -apple-system, Segoe UI, Roboto, Arial, sans-serif; color:#111; margin:32px; }
      h1 { font-size:20px; margin:0 0 2px; letter-spacing:.5px; }
      .sub { color:#555; font-size:12px; margin:0 0 20px; }
      h2 { font-size:12px; text-transform:uppercase; letter-spacing:1.5px; color:#666; margin:22px 0 8px; border-bottom:1px solid #ddd; padding-bottom:4px; }
      table { width:100%; border-collapse:collapse; font-size:13px; }
      td, th { padding:5px 4px; }
      .row { display:flex; justify-content:space-between; padding:4px 0; font-size:13px; border-bottom:1px dotted #e5e5e5; }
      .row .lbl { color:#333; } .row .val { font-variant-numeric:tabular-nums; font-weight:600; }
      .strong { font-weight:800; font-size:15px; border-bottom:2px solid #222; }
      .num { text-align:right; font-variant-numeric:tabular-nums; }
      .pos { color:#0a7d1b; } .neg { color:#b3261e; }
      thead td { font-size:10px; text-transform:uppercase; letter-spacing:1px; color:#888; border-bottom:1px solid #ccc; }
      tfoot td { font-weight:800; border-top:2px solid #222; }
      .foot { margin-top:28px; color:#999; font-size:11px; }
    </style></head><body>
      <h1>END-OF-DAY SHEET</h1>
      <p class="sub">${day.start} &rarr; ${day.end} &nbsp;·&nbsp; Central Time business day${operator ? ` &nbsp;·&nbsp; ${operator}` : ''}</p>

      <h2>Money</h2>
      <div class="row"><span class="lbl">Money In (real loads)</span><span class="val pos">${fmt$(money.in)}</span></div>
      <div class="row"><span class="lbl">Money Out (cashouts paid)</span><span class="val neg">${fmt$(money.out)}</span></div>
      <div class="row strong"><span class="lbl">Net (in − out)</span><span class="val ${money.net >= 0 ? 'pos' : 'neg'}">${money.net >= 0 ? '+' : '-'}${fmt$(Math.abs(money.net))}</span></div>
      <div class="row"><span class="lbl">Cashout fees collected</span><span class="val">${fmt$(money.fees)}</span></div>
      <div class="row"><span class="lbl">Admin adjustments (${money.adjCount})</span><span class="val">${fmt$(money.adjustments)}</span></div>
      <div class="row"><span class="lbl">Free play issued</span><span class="val">${fmt$(money.freePlay)}</span></div>
      <div class="row"><span class="lbl">Played back</span><span class="val">${fmt$(money.playedBack)}</span></div>
      <div class="row"><span class="lbl">Pending payouts (${money.pendingCount})</span><span class="val">${fmt$(money.pending)}</span></div>

      <h2>Games — Wagered vs Paid</h2>
      <table>
        <thead><tr><td>Game</td><td class="num">Wagered</td><td class="num">Paid</td><td class="num">House</td></tr></thead>
        <tbody>${gameRows}</tbody>
        <tfoot><tr><td>Total</td><td class="num">${fmt$(rows.total.wagered)}</td><td class="num">${fmt$(rows.total.paid)}</td><td class="num ${rows.total.margin >= 0 ? 'pos' : 'neg'}">${fmt$(rows.total.margin)}</td></tr></tfoot>
      </table>

      <p class="foot">Generated ${nowStamp()} · Betohalics admin</p>
    </body></html>`;
}

export default EndOfDaySheet;
